import { useEffect, useState } from "react";
import { CircleCheck, CirclePlay, Info, Timer } from "lucide-react";
import { NavLinks } from "@/components/navLinks";
import { UserNav } from "@/components/userNav";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { monitoringAtendentes } from "@/http/monitoringAtendentes";
import { useQuery } from "@tanstack/react-query";
import { CardInfo } from "@/components/cardInfo";
import { monitoringCards } from "@/http/monitoringCards";
import { monitoringEquipe } from "@/http/monitoringEquipe";
import { monitoringFases } from "@/http/monitoringFases";
import { TabOption } from "@/components/monitoring/TabOption";
import { FilterSetor } from "@/components/monitoring/FilterSetor";
import { FilterUnidade } from "@/components/monitoring/FilterUnidade";
import { Agentes } from "@/components/monitoring/Agentes";
import { Equipe } from "@/components/monitoring/Equipe";
import { Fases } from "@/components/monitoring/Fases";
import { Loading } from "@/components/monitoring/Loading";
import { Navigate } from "react-router-dom";
import { FilterSwitch } from "@/components/monitoring/FilterSwitch";
import isAtrasado from "@/utils/isAtrasado";
import getPrazo from "@/utils/getPrazo";
import { EmptyData } from "@/components/monitoring/EmptyData";
import { useQueryState } from "nuqs";
import { useRole } from "@/hooks/use-role";

export function Monitoring() {
  const [tabValue, setTabValue] = useQueryState("tab", {
    defaultValue: "atendentes",
  });
  const [filterFases] = useQueryState("fases", { defaultValue: "dia" });
  const [unidade, setUnidade] = useState("");
  const [setores, setSetores] = useState("");
  const { isAdmin, roleCheck } = useRole();

  console.log("unidade: ", unidade);

  const [apenasAtrasados, setApenasAtrasados] = useState(false);
  const [
    getMonitoringAtendentes_atrasados,
    setGetMonitoringAtendentes_atrasados,
  ] = useState([]);
  const [getMonitoringEquipe_atrasados, setGetMonitoringEquipe_atrasados] =
    useState([]);
  const [getMonitoringFases_atrasados, setGetMonitoringFases_atrasados] =
    useState([]);

  if (!isAdmin && roleCheck) return <Navigate to="/admin" replace />;

  const {
    data: getMonitoringAtendentes,
    isLoading: loadingMonitoringAtendentes,
  } = useQuery({
    queryKey: ["monitoringAtendentes", unidade, setores],
    queryFn: () => monitoringAtendentes(unidade, setores),
    retry: 1,
    refetchOnWindowFocus: false,
    refetchInterval: 60000,
  });

  const { data: getMonitoringEquipe, isLoading: loadingMonitoringEquipe } =
    useQuery({
      queryKey: ["monitoringEquipe", unidade, setores],
      queryFn: () => monitoringEquipe(unidade, setores),
      retry: 1,
      refetchOnWindowFocus: false,
      refetchInterval: 60000,
    });

  const { data: getMonitoringCards, isLoading: loadingMonitoringCards } =
    useQuery({
      queryKey: ["monitoringCards", unidade, setores],
      queryFn: () => monitoringCards(unidade, setores),
      retry: 1,
      refetchOnWindowFocus: false,
      refetchInterval: 60000,
    });

  const { data: getMonitoringFases, isLoading: loadingMonitoringFases } =
    useQuery({
      queryKey: ["monitoringFases", unidade, setores, filterFases],
      queryFn: () => monitoringFases(unidade, setores, filterFases),
      retry: 1,
      refetchOnWindowFocus: false,
      refetchInterval: 60000,
    });

  function handleFilter() {
    const data_em_andamento = getMonitoringFases.data_em_andamento.filter(
      (solicitacao) => {
        const prazo = getPrazo(solicitacao.setor, solicitacao.servico);
        return isAtrasado(prazo, solicitacao.createdAt);
      },
    );
    const data_finalizadas = [];
    const data_pendentes = getMonitoringFases.data_pendentes.filter(
      (solicitacao) => {
        const prazo = getPrazo(solicitacao.setor, solicitacao.servico);
        return isAtrasado(prazo, solicitacao.createdAt);
      },
    );

    setGetMonitoringFases_atrasados({
      data_em_andamento,
      data_finalizadas,
      data_pendentes,
    });

    const data_getMonitoringAtendentes = getMonitoringAtendentes.map(
      (atendente) => {
        const solicitacoes = atendente.solicitacoes.filter((solicitacao) => {
          const prazo = getPrazo(solicitacao.setor, solicitacao.servico);
          return isAtrasado(prazo, solicitacao.createdAt);
        });

        return { ...atendente, solicitacoes };
      },
    );

    setGetMonitoringAtendentes_atrasados(data_getMonitoringAtendentes);

    const data_getMonitoringEquipe = getMonitoringEquipe.map((atendente) => {
      const solicitacoes = atendente.solicitacoes.filter((solicitacao) => {
        const prazo = getPrazo(solicitacao.setor, solicitacao.servico);
        return isAtrasado(prazo, solicitacao.createdAt);
      });

      return { ...atendente, solicitacoes };
    });

    setGetMonitoringEquipe_atrasados(data_getMonitoringEquipe);
  }

  function aplicarFiltrosFrontend(data) {
    if (!data) return data;
    if (!unidade && !setores) return data;

    return {
      ...data,
      data_em_andamento:
        data.data_em_andamento?.filter((solicitacao) => {
          const setorFilter = !setores || solicitacao.setor === setores;
          const unidadeFilter = !unidade || solicitacao.unidade === unidade;
          return setorFilter && unidadeFilter;
        }) || [],
      data_pendentes:
        data.data_pendentes?.filter((solicitacao) => {
          const setorFilter = !setores || solicitacao.setor === setores;
          const unidadeFilter = !unidade || solicitacao.unidade === unidade;
          return setorFilter && unidadeFilter;
        }) || [],
      data_finalizadas:
        data.data_finalizadas?.filter((solicitacao) => {
          const setorFilter = !setores || solicitacao.setor === setores;
          const unidadeFilter = !unidade || solicitacao.unidade === unidade;
          return setorFilter && unidadeFilter;
        }) || [],
    };
  }

  useEffect(() => {
    if (
      !apenasAtrasados &&
      (getMonitoringAtendentes_atrasados.length > 0 ||
        getMonitoringEquipe_atrasados.length > 0 ||
        getMonitoringFases_atrasados.length > 0)
    ) {
      setGetMonitoringFases_atrasados({
        data_em_andamento: [],
        data_finalizadas: [],
        data_pendentes: [],
      });
      setGetMonitoringAtendentes_atrasados([]);
      setGetMonitoringEquipe_atrasados([]);
    }

    if (apenasAtrasados) {
      handleFilter();
    }
  }, [
    apenasAtrasados,
    getMonitoringAtendentes,
    getMonitoringEquipe,
    getMonitoringFases,
  ]);

  return (
    <div className="bg-white min-h-screen">
      <header className="h-[70px] border-b border-border bg-zinc-50 flex flex-row justify-between items-center px-6">
        <NavLinks />
        <UserNav />
      </header>
      <div className="py-8 mx-auto max-w-[1100px]">
        <div className="flex items-center gap-2">
          <p className="text-2xl font-bold tracking-tight">Monitoramento</p>
        </div>

        <div className="flex items-center justify-between mt-5">
          <div className="flex items-center gap-2">
            <TabOption
              label="Atendentes"
              onClick={setTabValue}
              value="atendentes"
              selected={tabValue}
            />
            <TabOption
              label="Equipe"
              onClick={setTabValue}
              value="equipe"
              selected={tabValue}
            />
            <TabOption
              label="Fases"
              onClick={setTabValue}
              value="fases"
              selected={tabValue}
            />
          </div>

          <div className="flex items-center gap-2">
            <FilterSetor value={setores} onChange={setSetores} />
            <FilterUnidade value={unidade} onChange={setUnidade} />
            <FilterSwitch
              label="Mostrar apenas atrasados"
              disabled={
                !getMonitoringAtendentes ||
                !getMonitoringEquipe ||
                !getMonitoringFases
              }
              isChecked={apenasAtrasados}
              setIsChecked={setApenasAtrasados}
            />
          </div>
        </div>

        <div className="mt-5 grid grid-cols-4 gap-4">
          <CardInfo
            value={getMonitoringCards?.count_pendente || 0}
            loading={loadingMonitoringCards}
            label="Pendentes"
            bgIconColor="bg-[#ffdebf]"
            icon={<Info className="size-4 text-[#f38b2b]" />}
          />
          <CardInfo
            value={getMonitoringCards?.count_em_andamento || 0}
            loading={loadingMonitoringCards}
            label="Em andamento"
            bgIconColor="bg-[#bfc6ff]"
            icon={<CirclePlay className="size-4 text-[#2b42f3]" />}
          />
          <CardInfo
            value={getMonitoringCards?.count_finalizado || 0}
            loading={loadingMonitoringCards}
            label="Finalizados"
            bgIconColor="bg-[#d6f9d0]"
            icon={<CircleCheck className="size-4 text-[#4fb83e]" />}
          />
          <CardInfo
            value={
              getMonitoringCards?.count_tempo
                ? `${Math.floor(parseFloat(getMonitoringCards.count_tempo))} ${getMonitoringCards.count_tempo.split(" ")[1]}`
                : "0 minutos"
            }
            loading={loadingMonitoringCards}
            label="Tempo Médio de Resposta"
            bgIconColor="bg-[#bfc6ff]"
            icon={<Timer className="size-4 text-[#2b42f3]" />}
          />
        </div>

        <Tabs
          defaultValue="atendentes"
          value={tabValue}
          className="mt-5 space-y-4"
        >
          <TabsContent value="atendentes">
            {getMonitoringAtendentes?.length === 0 && <EmptyData />}
            {loadingMonitoringAtendentes ? (
              <Loading />
            ) : (
              <Agentes
                unidadeId={unidade}
                data={
                  apenasAtrasados
                    ? getMonitoringAtendentes_atrasados
                    : getMonitoringAtendentes
                }
              />
            )}
          </TabsContent>
          <TabsContent value="equipe">
            {getMonitoringEquipe?.length === 0 && <EmptyData />}
            {loadingMonitoringEquipe ? (
              <Loading />
            ) : (
              <Equipe
                unidadeId={unidade}
                data={
                  apenasAtrasados
                    ? getMonitoringEquipe_atrasados
                    : getMonitoringEquipe
                }
              />
            )}
          </TabsContent>
          <TabsContent value="fases">
            {loadingMonitoringFases ? (
              <Loading />
            ) : (
              <Fases
                unidadeId={unidade}
                data={
                  apenasAtrasados
                    ? getMonitoringFases_atrasados
                    : aplicarFiltrosFrontend(getMonitoringFases)
                }
                apenasAtrasados={apenasAtrasados}
              />
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
